// Copyright 2026, Auxilio Technologies LLC

import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from './store/authStore';
import { hasPermission, RoleType, PermissionType, Permissions } from './config/rolesConfig';

import MainLayout from './components/layout/MainLayout';
import LandingPage from './pages/LandingPage';
import Dashboard from './pages/Dashboard';
import OrderBoard from './pages/OrderBoard';
import AddOrder from './pages/AddOrder';
import OrderDetail from './pages/OrderDetail';
import PrepQueue from './pages/PrepQueue';
import UserManagement from './pages/UserManagement';
import Settings from './pages/Settings';
import RouteBuilder from './pages/RouteBuilder';
import DriverView from './pages/DriverView';

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuthStore();
  if (!isAuthenticated) return <Navigate to="/" replace />;
  return <>{children}</>;
};

// Gate a route on a permission; redirect to dashboard if the role lacks it.
const Guard: React.FC<{ permission: PermissionType; children: React.ReactNode }> = ({ permission, children }) => {
  const { user } = useAuthStore();
  const role = (user?.role || 'viewer') as RoleType;
  if (!hasPermission(role, permission)) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
};

const App: React.FC = () => {
  const { isAuthenticated } = useAuthStore();

  return (
    <Router>
      <Routes>
        <Route path="/" element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <LandingPage />} />

        <Route
          path="/*"
          element={
            <ProtectedRoute>
              <MainLayout>
                <Routes>
                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route path="/orders" element={<Guard permission={Permissions.ORDERS_READ}><OrderBoard /></Guard>} />
                  <Route path="/orders/new" element={<Guard permission={Permissions.ORDERS_WRITE}><AddOrder /></Guard>} />
                  <Route path="/orders/:id" element={<Guard permission={Permissions.ORDERS_READ}><OrderDetail /></Guard>} />
                  <Route path="/prep" element={<Guard permission={Permissions.PREP_READ}><PrepQueue /></Guard>} />
                  <Route path="/routes" element={<Guard permission={Permissions.DISPATCH_READ}><RouteBuilder /></Guard>} />
                  <Route path="/driver" element={<Guard permission={Permissions.DRIVER_READ}><DriverView /></Guard>} />
                  <Route path="/admin/users" element={<Guard permission={Permissions.USERS_READ}><UserManagement /></Guard>} />
                  <Route path="/settings" element={<Settings />} />
                  <Route path="*" element={<Navigate to="/dashboard" replace />} />
                </Routes>
              </MainLayout>
            </ProtectedRoute>
          }
        />
      </Routes>
    </Router>
  );
};

export default App;
